import { useState, useEffect } from 'react';
import { Send, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: '+44', label: 'UK (+44)' },
  { code: '+1', label: 'US (+1)' },
  { code: '+353', label: 'IE (+353)' },
  { code: '+33', label: 'FR (+33)' },
  { code: '+49', label: 'DE (+49)' },
  { code: '+34', label: 'ES (+34)' },
  { code: '+39', label: 'IT (+39)' },
  { code: '+31', label: 'NL (+31)' },
  { code: '+61', label: 'AU (+61)' },
  { code: '+91', label: 'IN (+91)' },
];

export default function ContactSection() {
  const [dropdowns, setDropdowns] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    country_code: '+44',
    phone: '',
    service: '',
    message: '',
    budget: '',
    care_plan: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/contact/settings`)
      .then(r => r.json())
      .then(d => {
        if (d.dropdowns) {
          setDropdowns(d.dropdowns);
          setForm(prev => ({
            ...prev,
            service: d.dropdowns.service?.default_option || '',
            budget: d.dropdowns.budget?.default_option || '',
            care_plan: d.dropdowns.care_plan?.default_option || '',
          }));
        }
      })
      .catch(() => {});
  }, []);

  const serviceOpts = dropdowns?.service?.options || ['Website Design', 'Landing Page', 'Small Business Package', 'Online Presence Review', 'Other'];
  const budgetOpts = dropdowns?.budget?.options || ['Under £800', '£800 - £1,800', '£1,800 - £3,500', '£3,500+'];
  const carePlanOpts = dropdowns?.care_plan?.options || ['N/A — not interested right now', 'Basic Care (£60/mo)', 'Growth Care (£150/mo)', 'VIP Partnership (£350+/mo)', 'Not sure — tell me more'];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Please enter a valid email';
    if (form.phone.trim() && !/^[\d\s\-()]{6,15}$/.test(form.phone.trim())) errs.phone = 'Please enter a valid phone number';
    if (!form.message.trim()) errs.message = 'Please tell us about your project';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setStatus('loading');
    const payload = { ...form, phone: form.phone.trim() ? `${form.country_code} ${form.phone.trim()}` : '', agreed_to_terms: agreed };
    try {
      await axios.post(`${API}/contact`, payload);
      setStatus('success');
      setForm({ name: '', email: '', country_code: '+44', phone: '', service: '', message: '', budget: '', care_plan: '' });
      setErrors({});
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#00E5FF]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00E5FF]/3 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          {/* Left - CTA Text */}
          <div className="order-2 lg:order-1">
            <ScrollReveal>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-500 block mb-4">
                Start a Project
              </span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
                Let's Build a Website That Makes Your Business
                <span className="text-[#00E5FF]"> Look Its Best</span>
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-slate-400 mb-10">
                Leave your phone number and I will give you a call to chat through your project — or if you prefer email, just leave your message and I will reply within 24 hours.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="space-y-4 mb-10">
                {[
                  'Free initial consultation',
                  'No obligation quote',
                  'Response within 24 hours',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00E5FF]" />
                    <span className="text-sm font-medium text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <a href="mailto:info@uidesigns.co.uk" className="hover:text-[#00E5FF] transition-colors">info@uidesigns.co.uk</a>
                <span className="text-slate-700">·</span>
                <a href="tel:+447466981840" className="hover:text-[#00E5FF] transition-colors">+44 7466 981840</a>
              </div>
            </ScrollReveal>
          </div>

          {/* Right - Form (shows first on mobile) */}
          <div className="order-1 lg:order-2">
            <ScrollReveal delay={150}>
            {status === 'success' ? (
              <div data-testid="contact-success-message" className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="w-16 h-16 rounded-full bg-[#00E5FF]/20 flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-[#00E5FF]" />
                </div>
                <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-3">Message Sent!</h3>
                <p className="text-slate-400 mb-6">Thanks for reaching out. I will get back to you within 24 hours.</p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setStatus('idle')}
                  className="text-sm font-medium text-[#00E5FF] hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Name *
                    </label>
                    <input
                      data-testid="contact-name-input"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={`w-full bg-white/5 border rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all ${errors.name ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30' : 'border-white/10 focus:border-[#00E5FF]/50 focus:ring-[#00E5FF]/30'}`}
                    />
                    {errors.name && <p className="text-[11px] text-rose-400 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@business.com"
                      className={`w-full bg-white/5 border rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all ${errors.email ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30' : 'border-white/10 focus:border-[#00E5FF]/50 focus:ring-[#00E5FF]/30'}`}
                    />
                    {errors.email && <p className="text-[11px] text-rose-400 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="min-w-0">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Phone
                    </label>
                    <div className="flex gap-2 min-w-0">
                      <select
                        data-testid="contact-country-code"
                        name="country_code"
                        value={form.country_code}
                        onChange={handleChange}
                        className="w-[110px] bg-white/5 border border-white/10 rounded-xl px-2 py-3.5 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all appearance-none flex-shrink-0"
                      >
                        {COUNTRY_CODES.map(c => (
                          <option key={c.code} value={c.code} className="bg-[#0A0A0A]">{c.label}</option>
                        ))}
                      </select>
                      <input
                        data-testid="contact-phone-input"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="7466 981840"
                        className={`flex-1 min-w-0 bg-white/5 border rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all ${errors.phone ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30' : 'border-white/10 focus:border-[#00E5FF]/50 focus:ring-[#00E5FF]/30'}`}
                      />
                    </div>
                    {errors.phone && <p className="text-[11px] text-rose-400 mt-1">{errors.phone}</p>}
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Service
                    </label>
                    <select
                      data-testid="contact-service-select"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all appearance-none"
                    >
                      <option value="" className="bg-[#0A0A0A]">Select a service</option>
                      {serviceOpts.map((opt) => (
                        <option key={opt} value={opt} className="bg-[#0A0A0A]">{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Budget Range
                  </label>
                  <select
                    data-testid="contact-budget-select"
                    name="budget"
                    value={form.budget}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all appearance-none"
                  >
                    <option value="" className="bg-[#0A0A0A]">Select budget range</option>
                    {budgetOpts.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#0A0A0A]">{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Care Plan
                  </label>
                  <select
                    data-testid="contact-care-plan-select"
                    name="care_plan"
                    value={form.care_plan}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all appearance-none"
                  >
                    {carePlanOpts.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#0A0A0A]">{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Tell Me About Your Project *
                  </label>
                  <textarea
                    data-testid="contact-message-textarea"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="What kind of website do you need? What does your business do?"
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all resize-none ${errors.message ? 'border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/30' : 'border-white/10 focus:border-[#00E5FF]/50 focus:ring-[#00E5FF]/30'}`}
                  />
                  {errors.message && <p className="text-[11px] text-rose-400 mt-1">{errors.message}</p>}
                </div>

                {status === 'error' && (
                  <p data-testid="contact-error-message" className="text-sm text-rose-400">
                    Something went wrong. Please try again or email me directly.
                  </p>
                )}

                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white/5 border border-white/10">
                  <span className="font-['Outfit'] text-sm font-bold text-[#00E5FF]">Flexible pricing</span>
                  <span className="text-xs text-slate-400">tailored to your project</span>
                </div>

                <label data-testid="contact-consent-label" className="flex items-start gap-3 cursor-pointer group">
                  <input
                    data-testid="contact-consent-checkbox"
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#00E5FF] focus:ring-[#00E5FF]/30 focus:ring-offset-0 cursor-pointer accent-[#00E5FF]"
                  />
                  <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-400 transition-colors">
                    I agree to the{' '}
                    <Link to="/privacy-policy" className="text-[#00E5FF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/terms-of-service" className="text-[#00E5FF] hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>.
                  </span>
                </label>

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={status === 'loading' || !agreed}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-black px-8 py-4 rounded-full font-semibold transition-all hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </ScrollReveal>
          </div>
        </div>

        {/* Map — below everything on mobile, below form on desktop */}
        <div className="mt-12 lg:mt-16">
          <ScrollReveal delay={300}>
            <div data-testid="contact-map" className="rounded-xl overflow-hidden border border-white/10 aspect-video max-w-3xl mx-auto">
              <iframe
                title="UI Designs service area — Bournemouth, Poole & Christchurch"
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d80664.29353818498!2d-1.8799!3d50.7300!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2suk!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              />
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
